import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import TodayIcon from '@mui/icons-material/Today';
import { Box, Button, Divider, Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useGetUserCredentialsValidityAcQuery } from '../../apiRtk/accessControlApi';
import { ReactComponent as TimeLimitedIcon } from '../../assets/time-limited.svg';
import { useGetActualCompanyId } from '../../hooks/companyHooks';
import { useGetActualSiteId } from '../../hooks/siteHooks';
import { useModal } from '../../hooks/useModal';
import { EnrichedApartmentUser } from '../../modules/apartmentAdmin/data';
import { getApartmentDevices } from '../../modules/apartments/selectors';
import { VisitorModalContext, VisitorModalOpeningContext } from '../../modules/commonTypes';
import { getSiteData } from '../../modules/sites/selectors';
import { IconWithContent } from '../../pages/apartmentAdmin/ApartmentAdminUserDetailPage/components/UserAccessSettings/components/IconWithContent';
import VisitorModal from '../../pages/apartmentAdmin/ApartmentAdminUsersOverviewPage/components/VisitorModal/VisitorModal';
import { CustomDate } from '../index';
import RowSection from '../RowSection';
import { useGetGlobalHooks } from '../UserModal/hooks/getGlobalHooks';
import messages from './messages';

interface ISectionAccessValidity {
  user: EnrichedApartmentUser;
}

function UserAccessValidity({ user }: ISectionAccessValidity) {
  const [enrichedUser, setEnrichedUser] = useState(user);
  const { formatMessage } = useGetGlobalHooks();
  const isVisitorValiditySet = enrichedUser.validFrom ?? enrichedUser.validTo;
  const companyId = useGetActualCompanyId();
  const siteId = useGetActualSiteId();
  const siteData = useSelector(getSiteData);
  const apartmentDevices = useSelector(getApartmentDevices).data || [];

  const { data: userCredentialsValidity } = useGetUserCredentialsValidityAcQuery(
    {
      companyId,
      siteId,
      userId: user.id.toString(),
    },
    { refetchOnMountOrArgChange: true }
  );

  const { Modal: VisitorUModal, onOpen: onOpenVisitorUModal } = useModal({
    Modal: VisitorModal,
  });

  useEffect(() => {
    setEnrichedUser({
      ...user,
      validFrom: userCredentialsValidity?.validFrom,
      validTo: userCredentialsValidity?.validTo,
    });
  }, [userCredentialsValidity]);

  return (
    <>
      <RowSection
        icon={<IconWithContent Icon={TimeLimitedIcon}></IconWithContent>}
        label={formatMessage(messages.UserAccessValidityLabel)}
      >
        <Stack
          direction={{ sm: 'row', xs: 'column' }}
          justifyContent={{ sm: 'space-between', xs: 'flex-start' }}
          spacing={{ sm: 0, xs: 1 }}
          sx={{ width: 'inherit' }}
        >
          <>
            <Box>
              {isVisitorValiditySet ? (
                <>
                  {userCredentialsValidity?.validFrom && (
                    <>
                      <Box component="span" color={'text.secondary'}>
                        <FormattedMessage
                          {...messages.UserAccessValidityStart}
                          values={{
                            typography: (chunk) => <span>{chunk}</span>,
                            validityStart: (
                              <Typography component="span" color={'text.primary'}>
                                {CustomDate({ dateValue: userCredentialsValidity.validFrom, showTime: true })}
                              </Typography>
                            ),
                          }}
                        />
                      </Box>
                      &nbsp;
                    </>
                  )}
                  {userCredentialsValidity?.validTo && (
                    <Box component="span" color={'text.secondary'}>
                      <FormattedMessage
                        {...messages.UserAccessValidityEnd}
                        values={{
                          typography: (chunk) => <span>{chunk}</span>,
                          validityEnd: (
                            <Typography component="span" color={'text.primary'}>
                              {CustomDate({ dateValue: userCredentialsValidity.validTo, showTime: true })}
                            </Typography>
                          ),
                        }}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Typography color={'text.secondary'}>
                  <FormattedMessage {...messages.UserAccessNoValidityLimit} />
                </Typography>
              )}
              {userCredentialsValidity?.apartmentAccess && (
                <Stack direction={'row'} pt={1}>
                  <MeetingRoomIcon color="warning" />
                  <Typography ml={1}>
                    <FormattedMessage {...messages.UserAccessAprtmDirect} />
                  </Typography>
                </Stack>
              )}
            </Box>
            <Box>
              <Button onClick={onOpenVisitorUModal} startIcon={<TodayIcon />} variant="outlined">
                <FormattedMessage {...messages.UserAccessBtnValidityEdit} />
              </Button>
            </Box>
          </>
        </Stack>
      </RowSection>
      <Divider sx={{ mb: 3 }} />
      {companyId && siteId && (
        <VisitorUModal
          msgContext={VisitorModalContext.USER_ACCESS_VALIDITY_EDITMODAL}
          user={enrichedUser}
          apartmentInfo={{ companyId, siteId }}
          apartmentDevices={user?.apartments.length ? apartmentDevices : []}
          siteName={siteData?.name}
          userCredentialsValidity={userCredentialsValidity}
          openingContext={VisitorModalOpeningContext.USER_ACCESS_VALIDITY}
        />
      )}
    </>
  );
}

export default UserAccessValidity;
